import React, { useRef, useCallback, ChangeEvent, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';

// Context
import PageContext from 'Shared/contexts/PageContext';

// Constants
import { ItemType, WorkspaceItem, DataSourceSlugs } from 'Shared/constants/types';
import { pageContext } from 'Shared/constants/constants';

// Selectors
import { getMeetingItemByUuid } from 'Shared/selectors/common';
import { getItemsForStandardView } from 'Shared/selectors/workspace';

// Actions
import {
  removeTodoNesting,
  handleEmptyItemRemove,
  updateItem,
  addItem,
  disconnectAsanaTask,
  disconnectJiraIssue,
  disconnectServicenowRecord,
  toggleTodo,
  moveTodo,
} from 'Shared/actions/todos';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import { removeFromGoal } from 'Shared/actions/MeetingItems.actions';
import { fetchGoals } from 'Shared/actions/Goals.actions';

// Utils
import { dateMMMDDYYYY } from 'Shared/utils/DateTime.utils';
import { getHoveredItemUuid, MoveType } from './move-utils';
import { getSourcePage } from 'Shared/utils/getSourcePage';

// Hooks
import { useToggle, useDeepEqualSelector } from 'Shared/utils/hooks';
import { useGoal } from 'Shared/utils/hooks/use-goal';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Components
import { WpIcon } from 'Wpds/components/icon';
import CreateAsanaTaskModal from 'Shared/components/modals/AsanaModal/CreateAsanaTaskModal';
import CopyToJiraModal from 'Shared/components/modals/JiraModal/CopyToJiraModal';
import CopyToServicenowModal from 'Shared/components/modals/CopyToServicenowModal/CopyToServicenowModal';
import AddToGoalModal from 'Shared/components/modals/AddToGoalModal/AddToGoalModal';
import DuplicateModal from 'Shared/components/modals/DuplicateModal/DuplicateModal';
import RecurringItemModal from 'Shared/components/modals/RecurringItemModal/RecurringItemModal';
import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
} from 'Wpds/components/dropdown';
import BaseButton from 'Shared/components/MeetingItemsContainer/partials/BaseButton';

// Styles
import './ActionsMenu.scss';

type Props = {
  uuid: string;
  onAttachmentAdd: (event: ChangeEvent<HTMLInputElement>) => void;
  onArrowUpClick?: Function;
  onCommentsAreaClose?: Function;
  onHideAnimationTrigger?: Function;
};

const ActionsMenu = ({ uuid, onAttachmentAdd, onArrowUpClick, onCommentsAreaClose, onHideAnimationTrigger }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const currentPageContext = useContext(PageContext);
  const isGoalDetailPageContext = currentPageContext === pageContext.GOAL_DETAIL_PAGE;
  const isHomePageContext = currentPageContext === pageContext.HOME_PAGE;
  const isWorkspacePage = currentPageContext === pageContext.WORKSPACE_PAGE;

  const todo: WorkspaceItem = useSelector(getMeetingItemByUuid(uuid));
  const {
    parentUuid,
    itemType,
    isSection,
    recurringWorkspaceItem,
    dueBy,
    id,
    workspaceId,
    completed,
    asanaData,
    jiraData,
    servicenowData,
    goalSlug,
  } = todo;

  const [isCompleting, toggleIsCompleting] = useToggle(completed);
  const [isDuplicateModalOpen, toggleIsDuplicateModalOpen] = useToggle(false);
  const [isRecurringModalOpen, toggleIsRecurringModalOpen] = useToggle(false);
  const [isAsanaModalOpen, toggleIsAsanaModalOpen] = useToggle(false);
  const [isJiraModalOpen, toggleIsJiraModalOpen] = useToggle(false);
  const [isServicenowModalOpen, toggleIsServicenowModalOpen] = useToggle(false);
  const [isAddToGoalModalOpen, toggleIsAddToGoalModalOpen] = useToggle(false);

  const fileUploader = useRef(null);
  const toggleTimeout = useRef<number>(null);
  const hideAnimationTimeout = useRef<number>(null);

  const { goal } = useGoal({ slug: goalSlug, fetchOnMount: false });
  const { hasAsanaIntegration, hasJiraIntegration, id: userId } = useCurrentUser();
  const hasServicenowIntegration = useSelector(
    state => state.data_sources?.find(({ slug }) => slug === DataSourceSlugs.servicenow)?.connected
  );
  const isActionItem = itemType === ItemType.actionItem;
  const isTopicSection = isSection && !isActionItem;

  const isShowAttachFileButton = !isTopicSection;
  const isShowAddSubItemButton = !isHomePageContext && !parentUuid && isActionItem;
  const isShowRemoveIndentButton = !isHomePageContext && parentUuid && isActionItem;
  const allowGoalEditing = goal ? goal.assigneeId === userId || goal.assignerId === userId : false;
  const isShowConvertToButton = !isGoalDetailPageContext && !isHomePageContext && !isTopicSection;
  const isShowMoveButtons = !isGoalDetailPageContext && !isHomePageContext && !completed;
  const showAsanaActions = hasAsanaIntegration && !isTopicSection;
  const showJiraActions = hasJiraIntegration && !isTopicSection;
  const showServicenowActions = hasServicenowIntegration && !isTopicSection;

  const sortedTypedItems = useDeepEqualSelector(state =>
    isActionItem
      ? getItemsForStandardView(state.workspace.meeting_items)
      : getItemsForStandardView(state.workspace.discussion_topics)
  );

  const trackDropdownClick = useCallback((value: boolean) => {
    if (value)
      (window as any).analytics.track('Opened Item Menu', { source_page: getSourcePage(), item_type: 'actionsMenu' });
  }, []);

  const handleAttachmentAdd = useCallback(
    event => {
      onAttachmentAdd(event);
    },
    [onAttachmentAdd]
  );

  const handleFocusedTodoUpdate = useCallback(
    itemUuid => {
      if (isWorkspacePage) {
        const path = `/workspaces/${workspaceId}/item/${itemUuid}`;
        history.push(path);
      }
      dispatch(updateSelectedUuids(itemUuid, { isOneSelected: true }));
    },
    [dispatch, history, workspaceId, isWorkspacePage]
  );

  const handleToggleCompleted = useCallback(() => {
    toggleIsCompleting();
    if (toggleTimeout.current) clearTimeout(toggleTimeout.current);
    if (hideAnimationTimeout.current) clearTimeout(hideAnimationTimeout.current);
    if (onHideAnimationTrigger && !toggleIsCompleting)
      hideAnimationTimeout.current = window.setTimeout(onHideAnimationTrigger, 200);
    const timeoutDispatch = onHideAnimationTrigger ? 800 : 400;
    toggleTimeout.current = window.setTimeout(() => {
      dispatch(toggleTodo({ id, uuid, itemType }, !isCompleting));
    }, timeoutDispatch);
  }, [isCompleting, id, uuid, dispatch, itemType, onHideAnimationTrigger]);

  const handleSubItemAdd = () => {
    const itemUuid = uuidv4();
    dispatch(
      addItem({
        parentId: id,
        parentUuid: uuid,
        itemUuid,
        workspaceId,
        itemType,
        goalSlug: isGoalDetailPageContext ? goalSlug : null,
      })
    );
    setTimeout(() => handleFocusedTodoUpdate(itemUuid), 0);
  };

  const handleFileUploadIconClick = useCallback(() => fileUploader.current.click(), [fileUploader]);

  const handleRecurringDiscussionTopicClick = useCallback(() => {
    const routeUuid = uuid;
    let path = location.pathname;
    // need to check and see if the panel is already open - if so, we need
    // strip the existing uuid off the route before appending the new one
    if (path.includes('/item/')) {
      path = path.split('/item/')[0];
    }

    // check for trailing slash
    path = path.slice(-1) === '/' ? path.slice(0, -1) : path;

    history.push({
      pathname: `${path}/item/${routeUuid}/comments`,
      state: { modal: location.state?.modal, preventPreviousRouteChange: location.state?.modal },
    });
  }, [uuid]);

  const handleRemoveItemFromGoal = useCallback(() => {
    dispatch(removeFromGoal(uuid));
  }, []);

  const handleRemoveNesting = useCallback(() => dispatch(removeTodoNesting({ uuid, itemType })), [
    uuid,
    itemType,
    dispatch,
  ]);

  const handleToggleDiscussionTopicAndActionItem = useCallback(() => {
    const convertedActionItemDueBy =
      itemType === ItemType.actionItem || dueBy
        ? dueBy
        : moment(new Date()).startOf('day').startOf('hour').add(7, 'days').add(12, 'hours').unix();
    dispatch(
      updateItem(uuid, {
        itemType,
        newItemType: itemType === ItemType.discussionTopic ? ItemType.actionItem : ItemType.discussionTopic,
        dueBy: convertedActionItemDueBy,
      })
    );

    handleFocusedTodoUpdate(uuid);
  }, [dispatch, uuid, dueBy, itemType, handleFocusedTodoUpdate]);

  const handleMoveToTopClick = async () => {
    const hoverOffset = 0;
    const hoveredItemUuid = getHoveredItemUuid({ meetingItem: todo, sortedTypedItems, moveType: MoveType.toTop });
    if (!hoveredItemUuid) return;
    await dispatch(moveTodo(itemType, { draggedItemUuids: [uuid], hoveredItemUuid, hoverOffset }));
  };

  const handleMoveToBottomClick = async () => {
    const hoverOffset = 1;
    const hoveredItemUuid = getHoveredItemUuid({ meetingItem: todo, sortedTypedItems, moveType: MoveType.toBottom });
    if (!hoveredItemUuid) return;
    await dispatch(moveTodo(itemType, { draggedItemUuids: [uuid], hoveredItemUuid, hoverOffset }));
  };

  const handleDisconnectFromAsanaclick = useCallback(() => {
    dispatch(disconnectAsanaTask({ uuid, itemType }));
  }, [dispatch, itemType, uuid]);

  const handleDisconnectFromJiraclick = useCallback(() => {
    dispatch(disconnectJiraIssue({ uuid, itemType }));
  }, [dispatch, itemType, uuid]);

  const handleDisconnectFromServicenowClick = useCallback(() => {
    dispatch(disconnectServicenowRecord({ uuid, itemType }));
  }, [dispatch, itemType, uuid]);

  const handleRemoveClick = useCallback(() => {
    const isCommentsWrapperOpen = location?.pathname?.includes('/item/') && location?.pathname?.includes('/comments');

    const removed = dispatch(handleEmptyItemRemove(uuid));
    if (removed && onArrowUpClick) onArrowUpClick({ uuid });

    if (onCommentsAreaClose && isCommentsWrapperOpen) {
      onCommentsAreaClose();
    }
  }, [dispatch, onArrowUpClick, uuid]);

  const handleFetchGoals = useCallback((value: boolean) => {
    if (value) {
      dispatch(
        fetchGoals({
          context: 'workspace',
          workspace_slug: workspaceId,
        })
      );
    }
  }, []);

  const baseClassName = 'ActionsMenu';

  return (
    <>
      <input style={{ display: 'none' }} type="file" ref={fileUploader} onChange={handleAttachmentAdd} value="" />
      {isDuplicateModalOpen && (
        <DuplicateModal itemUuids={[uuid]} isOpen={isDuplicateModalOpen} onToggle={toggleIsDuplicateModalOpen} />
      )}
      {isRecurringModalOpen && <RecurringItemModal itemUuid={uuid} onToggle={toggleIsRecurringModalOpen} />}
      {isAsanaModalOpen && <CreateAsanaTaskModal isOpen onToggle={toggleIsAsanaModalOpen} itemUuid={uuid} />}
      {isJiraModalOpen && <CopyToJiraModal isOpen onToggle={toggleIsJiraModalOpen} itemUuid={uuid} />}
      {isServicenowModalOpen && <CopyToServicenowModal isOpen onToggle={toggleIsServicenowModalOpen} itemUuid={uuid} />}
      {isAddToGoalModalOpen && <AddToGoalModal isOpen onToggle={toggleIsAddToGoalModalOpen} itemUuid={uuid} />}

      <WpDropdown
        placement="left-start"
        onClickCallback={(value: boolean) => {
          handleFetchGoals(value);
          trackDropdownClick(value);
        }}
        withMaxHeight={false}
      >
        <WpDropdownToggle>
          <BaseButton icon="ellipsis-h-alt" tooltip="More options" qaTest="ActionsMenu-toggle-btn" />
        </WpDropdownToggle>
        <WpDropdownMenu className={`${baseClassName}__dropdown-menu`}>
          <li className={`${baseClassName}__header`}>
            <div className="actions-menu-info-label">{`ADDED: ${dateMMMDDYYYY(todo.createdAt)}`}</div>
            <div className="actions-menu-info-label">{`BY: ${todo.user.fullName || todo.user.email}`}</div>
          </li>
          <WpDropdownMenuDivider />
          {isSection && (
            <WpDropdownMenuItem onClick={handleToggleCompleted}>
              <WpIcon name="check-circle" />
              {completed ? 'Mark as incomplete' : 'Mark as complete'}
            </WpDropdownMenuItem>
          )}
          {isShowAddSubItemButton && (
            <>
              <WpDropdownMenuItem onClick={handleSubItemAdd}>
                <WpIcon name="arrow-right" />
                Add subitem
              </WpDropdownMenuItem>
              <WpDropdownMenuDivider />
            </>
          )}
          {isShowAttachFileButton && (
            <WpDropdownMenuItem onClick={handleFileUploadIconClick}>
              <WpIcon name="paperclip" aria-hidden="true" />
              Attach file
            </WpDropdownMenuItem>
          )}
          {isActionItem && (
            <WpDropdownMenuItem onClick={toggleIsRecurringModalOpen} closeOnClick>
              <WpIcon name="recycle" />
              {`${recurringWorkspaceItem ? 'Edit' : 'Create'} recurring ${isActionItem ? 'item' : 'topic'}`}
            </WpDropdownMenuItem>
          )}
          {!isActionItem && !isSection && (
            <WpDropdownMenuItem onClick={handleRecurringDiscussionTopicClick}>
              <WpIcon name="recycle" />
              {`${recurringWorkspaceItem ? 'Edit' : 'Create'} recurring ${isActionItem ? 'item' : 'topic'}`}
            </WpDropdownMenuItem>
          )}
          {!isGoalDetailPageContext && !goalSlug && (
            <WpDropdownMenuItem onClick={toggleIsDuplicateModalOpen}>
              <WpIcon name="copy" />
              Move/copy to another workspace
            </WpDropdownMenuItem>
          )}
          {!isGoalDetailPageContext && isActionItem && (
            <WpDropdownMenuItem onClick={toggleIsAddToGoalModalOpen}>
              <WpIcon name="bullseye-arrow" />
              {goalSlug ? 'Change goal' : 'Add to goal'}
            </WpDropdownMenuItem>
          )}
          {isActionItem && goalSlug && allowGoalEditing && (
            <WpDropdownMenuItem onClick={handleRemoveItemFromGoal}>
              <WpIcon name="minus-square" />
              Remove from goal
            </WpDropdownMenuItem>
          )}
          <WpDropdownMenuDivider />
          {isShowMoveButtons && (
            <>
              <WpDropdownMenuItem onClick={handleMoveToTopClick} qaTest="ActionsMenu-item-move-to-top">
                <WpIcon name="arrow-to-top" />
                Move to top
              </WpDropdownMenuItem>
              <WpDropdownMenuItem onClick={handleMoveToBottomClick} qaTest="ActionsMenu-item-move-to-bottom">
                <WpIcon name="arrow-to-bottom" />
                Move to bottom
              </WpDropdownMenuItem>
            </>
          )}
          {isShowConvertToButton && (
            <WpDropdownMenuItem
              onClick={handleToggleDiscussionTopicAndActionItem}
              qaTest="ActionsMenu-item-convet-ai-dt"
            >
              <WpIcon name="check-circle" />
              {isActionItem ? 'Convert to discussion topic' : 'Convert to action item'}
            </WpDropdownMenuItem>
          )}
          {isShowRemoveIndentButton && (
            <WpDropdownMenuItem onClick={handleRemoveNesting}>
              <WpIcon name="arrow-left" />
              Remove indent
            </WpDropdownMenuItem>
          )}
          {showAsanaActions && (
            <>
              {asanaData ? (
                <WpDropdownMenuItem onClick={handleDisconnectFromAsanaclick}>
                  <WpIcon name="unlink" />
                  Disconnect from Asana
                </WpDropdownMenuItem>
              ) : (
                <WpDropdownMenuItem onClick={toggleIsAsanaModalOpen}>
                  <WpIcon name="copy" />
                  Copy to Asana
                </WpDropdownMenuItem>
              )}
            </>
          )}
          {showJiraActions && (
            <>
              {jiraData ? (
                <WpDropdownMenuItem onClick={handleDisconnectFromJiraclick}>
                  <WpIcon name="jira" prefix="fab" />
                  Disconnect from Jira
                </WpDropdownMenuItem>
              ) : (
                <WpDropdownMenuItem onClick={toggleIsJiraModalOpen}>
                  <WpIcon name="jira" prefix="fab" />
                  Copy to Jira
                </WpDropdownMenuItem>
              )}
            </>
          )}
          {showServicenowActions && (
            <>
              {servicenowData ? (
                <WpDropdownMenuItem onClick={handleDisconnectFromServicenowClick}>
                  <WpIcon name="unlink" />
                  Disconnect from ServiceNow
                </WpDropdownMenuItem>
              ) : (
                <WpDropdownMenuItem onClick={toggleIsServicenowModalOpen}>
                  <WpIcon name="copy" />
                  Copy to ServiceNow
                </WpDropdownMenuItem>
              )}
            </>
          )}
          <WpDropdownMenuDivider />
          <WpDropdownMenuItem onClick={handleRemoveClick}>
            <WpIcon name="trash-alt" />
            Delete item
          </WpDropdownMenuItem>
        </WpDropdownMenu>
      </WpDropdown>
    </>
  );
};

export default React.memo(ActionsMenu);
